import React from 'react';
import { Card, CardContent, Typography, Button, Box, Chip, Divider, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlaceIcon from '@mui/icons-material/Place';
import PhoneIcon from '@mui/icons-material/Phone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Define the Framer Motion variants for the hover effect
const cardMotion = {
    rest: { y: 0, scale: 1, boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.05)' },
    hover: { y: -5, scale: 1.01, boxShadow: '0px 10px 20px rgba(0, 0, 0, 0.15)' }
};

// Function to map fit score to a color
const getFitScoreColor = (score) => {
    // Standardizing the color mapping based on match score
    if (score.includes('match') || score.includes('Excellent') || score.includes('High Rating')) {
        return 'success';
    }
    if (score.includes('Nearby') || score.includes('Recommended') || score.includes('Affordable')) {
        return 'primary';
    }
    if (score.includes('Strong') || score.includes('Popular') || score.includes('Broad Coverage')) {
        return 'secondary';
    }
    if (score.includes('Crucial') || score.includes('High Priority')) {
        return 'warning';
    }
    return 'default';
};

// Component to handle dynamic image display
const ResourceLogo = ({ resource }) => {
    const theme = useTheme();
    // Path logic assumes logos are in the public folder (or handled by your asset pipeline)
    const publicPath = resource.logoPath ? resource.logoPath.replace('/src/assets', '') : null;
    
    // Fallback based on type (using Material Icons)
    const fallbackIcon = 
        resource.type.includes("Hotline") ? <PhoneIcon sx={{ fontSize: 32, color: theme.palette.secondary.main }} /> : 
        resource.type.includes("Benefit") || resource.type.includes("Insurance") ? <CheckCircleIcon sx={{ fontSize: 32, color: theme.palette.success.main }} /> :
        <PlaceIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />;

    return (
        <Box sx={{ 
            height: 60, // Fixed height for logo section
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            bgcolor: theme.palette.background.default,
            borderBottom: `1px solid ${theme.palette.divider}`,
            p: 1
        }}>
            {publicPath ? (
                <img 
                    src={publicPath} 
                    alt={`${resource.title} Logo`}
                    style={{ 
                        maxHeight: '40px', 
                        maxWidth: '90%', 
                        objectFit: 'contain'
                    }} 
                    onError={(e) => { e.target.style.display = 'none'; e.target.parentNode.innerHTML = fallbackIcon.props.children }}
                />
            ) : (
                fallbackIcon
            )}
        </Box>
    );
};


export const ResourceCard = ({ resource, index, onViewDetails }) => {
    const theme = useTheme();
    
    const handleCardClick = (e) => {
        // Prevent button click from double-firing the card click
        if (e.target.tagName !== 'BUTTON' && e.target.closest('button')) {
             return;
        }
        onViewDetails(resource);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
        >
            <motion.div 
                variants={cardMotion}
                initial="rest"
                whileHover="hover"
                whileTap={{ scale: 0.98 }} 
            >
                <Card 
                    sx={{ 
                        borderRadius: 3, 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column',
                        cursor: 'pointer',
                        transition: 'box-shadow 0.3s'
                    }}
                    onClick={handleCardClick} // Card click opens details
                >
                    {/* 1. Logo/Icon Section (Fixed Height: 60px) */}
                    <ResourceLogo resource={resource} />

                    <CardContent sx={{ flexGrow: 1, pb: 1, display: 'flex', flexDirection: 'column' }}>
                        
                        {/* 2. Title and Rating */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, minHeight: '40px' }}>
                            <Typography 
                                variant="subtitle1" 
                                component="div" 
                                sx={{ 
                                    fontWeight: 700, 
                                    color: theme.palette.text.primary, 
                                    lineHeight: 1.2,
                                    flexGrow: 1,
                                    display: '-webkit-box',
                                    overflow: 'hidden',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    mr: 1
                                }}
                            >
                                {resource.title}
                            </Typography>
                            <Chip 
                                label={resource.fitScore} 
                                size="small"
                                color={getFitScoreColor(resource.fitScore)}
                                sx={{ fontWeight: 600, flexShrink: 0 }}
                            />
                        </Box>
                        
                        {/* 3. Description (FIXED MINIMUM HEIGHT) */}
                        <Box sx={{ minHeight: '60px', mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" 
                                sx={{
                                    display: '-webkit-box',
                                    overflow: 'hidden',
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: 'vertical',
                                }}
                            >
                                {resource.description}
                            </Typography>
                        </Box>

                        <Divider sx={{ mb: 1.5, mt: 'auto' }} />
                        
                        {/* 4. Tags and Quick Info (FIXED MINIMUM HEIGHT) */}
                        <Box sx={{ minHeight: '50px', mb: 1 }}>
                            <Box sx={{ mb: 1 }}>
                                {resource.tags.slice(0, 3).map((tag) => (
                                    <Chip 
                                        key={tag} 
                                        label={tag} 
                                        size="small" 
                                        sx={{ 
                                            mr: 0.5, 
                                            mb: 0.5, 
                                            bgcolor: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[800]
                                        }} 
                                    />
                                ))}
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <PlaceIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                <Typography variant="caption" color="text.secondary" noWrap>
                                    {resource.address.split(',')[0]}
                                </Typography>
                            </Box>
                        </Box>

                    </CardContent>
                    
                    {/* 5. "View Details" Button (Always at the bottom) */}
                    <Box sx={{ p: 2, pt: 1, mt: 0 }}>
                        <Button 
                            variant="outlined" 
                            color="primary" 
                            fullWidth 
                            endIcon={<ArrowForwardIcon />}
                            onClick={handleCardClick}
                        >
                            View Details
                        </Button>
                    </Box>
                </Card>
            </motion.div>
        </motion.div>
    );
};